/**
 * Admin: Live Lessons (spec 9, MANUAL in v1). Admin and root only; every
 * number comes from the live lessons admin callables. Nothing here acts on
 * its own: there are no automated consequences.
 *
 * - Monitoring: every event aggregated all time, by month, by coach, by
 *   player and by player per month, with counts and miss rates
 *   (getLiveMonitoring). Lessons count in their own month, in the player's
 *   zone.
 * - The alert feed: each event as it happens, newest first, worded by the
 *   server (getLiveAlertFeed).
 * - Coaches: each coach's counts, open slots against members' unused
 *   credits, members against the cap and the members falling behind; the
 *   coach selected shows those members, the coach's events and Deduct From
 *   Next Payout (getLiveCoachView).
 * - Memberships: pick one for its view (credits, credits from Double Bagel,
 *   billing, payouts in their two parts, lessons) and its buttons: add
 *   credits, restore credit, waive a deduction, pause or resume billing,
 *   offer transfer, move membership, refund remaining credits.
 *
 * Every button opens a confirmation with a required note; the server checks
 * the action, applies it and writes the audit record.
 *
 * The admin panel's layout (a 1200 column, white cards, tables), with the
 * shared capsule buttons and the type scale.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { getUserProfile } from '../../services/userProfile';
import {
  getLiveAlertFeed, getLiveCoachView, getLiveMembershipAdminView, getLiveMonitoring, liveLessonsAdminAction,
  liveLessonsErrorMessage,
  type LiveAdminAction, type LiveAdminActionParams, type LiveAlertEvent, type LiveCoachView, type LiveCounts,
  type LiveMembershipAdminView, type LiveMonitoring,
} from '../../services/liveLessons';
import { useToast } from '../../hooks/useToast';
import { formatPriceExact } from '../../utils/formatPrice';
import Sheet from '../../components/Sheet';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_SECONDARY_SM } from '../../components/buttonStyles';
import ButtonSpinner from '../../components/ButtonSpinner';
import { busyStyle } from '../../utils/busyStyle';
import { Skeleton } from '../../components/Skeleton';
import SegmentedControl from '../../components/SegmentedControl';

const CARD = 'rounded-[12px] border border-gray-200 bg-white';
const H2 = 'text-[17px] leading-[22px] font-semibold text-gray-900';
const FOOTNOTE = 'text-[13px] leading-[18px] text-gray-500';
const TH = 'px-4 py-2 text-left text-[12px] leading-[16px] font-semibold uppercase tracking-[0.5px] text-gray-500';
const TD = 'px-4 py-2.5 text-[15px] leading-[20px] text-gray-900 tabular-nums';
const DANGER = '#DC2626';
const LIVE_TYPES = ['free_intro_call', 'single_live_lesson', 'live_membership'];
const FEED_PAGE = 50;

function when(iso?: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "2026-12" as "Dec 2026". */
function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

const pct = (r: number) => `${Math.round(r * 100)}%`;

/** The monitoring columns, in the spec's order: lessons, misses, rates, then each event. */
const COUNT_COLUMNS: Array<{ key: keyof LiveCounts; label: string; rate?: boolean }> = [
  // Miss rate first, so a phone sees it without scrolling (redesign spec, batch 8).
  { key: 'missRate', label: 'Miss rate', rate: true },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'completed', label: 'Completed' },
  { key: 'playerMisses', label: 'Player misses' },
  { key: 'coachMisses', label: 'Coach misses' },
  { key: 'playerMissRate', label: 'Player rate', rate: true },
  { key: 'coachMissRate', label: 'Coach rate', rate: true },
  { key: 'noShows', label: 'No-shows' },
  { key: 'lateJoins', label: 'Late joins' },
  { key: 'lateJoinMinutes', label: 'Late minutes' },
  { key: 'lateCancels', label: 'Late cancels' },
  { key: 'cancelsWithNotice', label: 'Cancels with notice' },
  { key: 'timeRequests', label: 'Time requested' },
  { key: 'moves', label: 'Moves' },
  { key: 'requestsUnanswered', label: 'Requests unanswered' },
  { key: 'adjustments', label: 'Adjustments' },
  { key: 'returned', label: 'Returned' },
  { key: 'paymentFailures', label: 'Payment failures' },
  { key: 'lapses', label: 'Lapses' },
];

type Scope = 'month' | 'coach' | 'player' | 'playerMonth';

/** A coach view event in a few words; the alert feed carries the server's own wording. */
function eventText(e: LiveCoachView['events'][number]): string {
  const d = e.data ?? {};
  if (e.type === 'late_join') return `Late join: ${String(d.side)}, ${String(d.minutes)} minutes`;
  if (e.type === 'cancel') return `Cancel by the ${String(d.actor)}, ${d.withNotice ? 'with notice' : 'inside 10 hours'}`;
  const t = e.type.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function CountsRow({ label, c, strong }: { label: string; c: LiveCounts; strong?: boolean }) {
  return (
    <tr className="border-t border-gray-100">
      <td className={`${TD} whitespace-nowrap ${strong ? 'font-semibold' : 'font-medium'}`}>{label}</td>
      {COUNT_COLUMNS.map((col) => (
        <td key={col.key} className={`${TD} text-right`}
          style={col.key === 'coachMisses' && c.coachMisses ? { color: DANGER } : undefined}>
          {col.rate ? pct(c[col.key]) : c[col.key]}
        </td>
      ))}
    </tr>
  );
}

interface CoachRow { coachId: string; name: string; v: LiveCoachView | null; error?: boolean }
interface MembershipRow {
  id: string; playerName: string; coachName: string; coachId: string; status: string;
  creditsUsed: number; creditsRemaining: number; cycleEndAt: string;
}

// ── Actions ───────────────────────────────────────────────────────────────────

const ACTION_TEXT: Record<LiveAdminAction, { title: string; body: string; destructive?: boolean }> = {
  add_credits: { title: 'Add Credits', body: 'Adds credits from Double Bagel to this Monthly Coaching. They never pay out to the coach and never roll over.' },
  restore_credit: { title: 'Restore Credit', body: 'Gives the player one credit back on this Monthly Coaching.' },
  deduct_from_next_payout: { title: 'Deduct From Next Payout', body: "Takes this amount off the coach's next payout.", destructive: true },
  waive_deduction: { title: 'Waive Deduction', body: 'Cancels this pending deduction before it is applied.' },
  pause_billing: { title: 'Pause Billing', body: 'Stops charging this Monthly Coaching until billing is resumed.', destructive: true },
  resume_billing: { title: 'Resume Billing', body: 'Charges this Monthly Coaching again from its next cycle date.' },
  offer_transfer: { title: 'Offer Transfer', body: 'Sends the player a message naming the coaches offered.' },
  move_membership: { title: 'Move Monthly Coaching', body: 'Moves the Monthly Coaching, its credits and its locked price to another coach.', destructive: true },
  refund_remaining_credits: { title: 'Refund Remaining Credits', body: 'Refunds the value of the credits left and deducts it from the coach.', destructive: true },
};

function ActionSheet({
  params, coaches, onClose, onDone,
}: {
  params: Omit<LiveAdminActionParams, 'note' | 'confirm'>;
  coaches: CoachRow[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { showSuccess } = useToast();
  const text = ACTION_TEXT[params.action];
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [count, setCount] = useState(1);
  const [period, setPeriod] = useState<'current' | 'next'>('current');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsAmount = params.action === 'deduct_from_next_payout';
  const pickMany = params.action === 'offer_transfer';
  const pickOne = params.action === 'move_membership';
  const grant = params.action === 'add_credits';
  const others = coaches.filter((c) => c.coachId !== params.coachId);

  const ready = note.trim().length > 0 &&
    (!needsAmount || Number(amount) > 0) &&
    (!pickMany || picked.length > 0) &&
    (!pickOne || picked.length === 1);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await liveLessonsAdminAction({
        ...params,
        note: note.trim(),
        confirm: true,
        ...(needsAmount && { amountCents: Math.round(Number(amount) * 100) }),
        ...(pickMany && { coachIds: picked }),
        ...(pickOne && { toCoachId: picked[0] }),
        ...(grant && { count, period }),
      });
      showSuccess(`${text.title}: done.`);
      onDone();
    } catch (err) {
      // The server's refusal, as a sentence.
      const said = liveLessonsErrorMessage(err, 'The action did not go through. Please try again.');
      setError(/[.?]$/.test(said) ? said : `${said}.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={text.title} onClose={onClose} busy={busy}>
      <p className="text-[15px] leading-[20px] text-db-text-1">{text.body}</p>

      {needsAmount && (
        <label className="mt-6 flex h-[52px] items-center gap-3 rounded-[12px] bg-white px-4">
          <span className="text-[17px] leading-[22px] text-db-text-1">Amount</span>
          <span className="ml-auto text-[17px] text-db-text-2">$</span>
          <input type="text" inputMode="decimal" value={amount} placeholder="0.00"
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
            className="w-24 bg-transparent text-right text-[17px] tabular-nums text-db-text-1 outline-none placeholder:text-db-text-3" />
        </label>
      )}

      {grant && (
        <div className="mt-6">
          <SegmentedControl<'current' | 'next'>
            ariaLabel="Period"
            value={period}
            onChange={setPeriod}
            segments={[{ value: 'current', label: 'This Period' }, { value: 'next', label: 'Next Period' }]}
          />
          <label className="mt-4 flex h-[52px] items-center gap-3 rounded-[12px] bg-white px-4">
            <span className="text-[17px] leading-[22px] text-db-text-1">Credits</span>
            <select value={count} onChange={(e) => setCount(Number(e.target.value))}
              className="ml-auto cursor-pointer appearance-none bg-transparent text-right text-[17px] tabular-nums text-db-text-2 focus:outline-none focus-visible:text-biscayne">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <p className={`mt-2 px-1 ${FOOTNOTE}`}>
            {period === 'current'
              ? 'Usable now, spent before paid credits, gone at the end of this period.'
              : 'Added when the next period starts, once its renewal is paid.'}
          </p>
        </div>
      )}

      {(pickMany || pickOne) && (
        <div className="mt-6">
          <p className="mb-2 text-[13px] leading-[18px] uppercase tracking-[0.5px] text-db-text-2">
            {pickMany ? 'Coaches to offer (up to 5)' : 'Move to'}
          </p>
          <div className="overflow-hidden rounded-[12px] bg-white">
            {others.length === 0 && <p className={`px-4 py-3 ${FOOTNOTE}`}>No other live coaches.</p>}
            {others.map((c, i) => {
              const on = picked.includes(c.coachId);
              return (
                <label key={c.coachId} className={`flex h-[52px] cursor-pointer items-center gap-3 px-4 ${i ? 'border-t border-[#e6e6e6]' : ''}`}>
                  <input
                    type={pickOne ? 'radio' : 'checkbox'}
                    name="coach"
                    checked={on}
                    onChange={() => setPicked((p) => pickOne ? [c.coachId]
                      : on ? p.filter((x) => x !== c.coachId) : p.length < 5 ? [...p, c.coachId] : p)}
                    className="h-4 w-4 accent-[#1A6B8A]"
                  />
                  <span className="text-[17px] leading-[22px] text-db-text-1">{c.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-[12px] bg-white">
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
          placeholder="Note (required): why, and anything the next person should know"
          aria-label="Note"
          className="block w-full resize-none bg-transparent px-4 py-3 text-[17px] leading-[22px] text-db-text-1 placeholder:text-db-text-3 focus:outline-none" />
      </div>
      <p className={`mt-2 px-1 ${FOOTNOTE}`}>The note is saved with the action in the audit log.</p>

      {error && <p className="mt-3 text-[13px] leading-[18px]" style={{ color: DANGER }}>{error}</p>}
      <button type="button" onClick={confirm} disabled={!ready || busy} aria-busy={busy}
        style={{ ...busyStyle(busy), ...(text.destructive ? { color: DANGER } : {}) }}
        className={`${text.destructive ? BTN_SECONDARY : BTN_PRIMARY} mt-6 w-full`}>
        {busy ? <ButtonSpinner /> : `Confirm ${text.title}`}
      </button>
    </Sheet>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AdminLiveLessons() {
  const [coaches, setCoaches] = useState<CoachRow[] | null>(null);
  const [coachError, setCoachError] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<string | null>(null);
  const [monitoring, setMonitoring] = useState<LiveMonitoring | null>(null);
  const [monitoringError, setMonitoringError] = useState(false);
  const [scope, setScope] = useState<Scope>('month');
  const [feed, setFeed] = useState<LiveAlertEvent[] | null>(null);
  const [feedError, setFeedError] = useState(false);
  const [feedMore, setFeedMore] = useState(false);
  const [feedBusy, setFeedBusy] = useState(false);
  const [names, setNames] = useState<Record<string, string>>({});
  const [memberships, setMemberships] = useState<MembershipRow[] | null>(null);
  const [selectedMembership, setSelectedMembership] = useState<string | null>(null);
  const [view, setView] = useState<LiveMembershipAdminView | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [action, setAction] = useState<Omit<LiveAdminActionParams, 'note' | 'confirm'> | null>(null);

  const loadCoaches = useCallback(() => {
    getDocs(query(collection(db, 'coachOfferings'), where('offeringType', 'in', LIVE_TYPES)))
      .then((offerings) => {
        const ids = [...new Set(offerings.docs.map((d) => d.data().coachId as string))];
        return Promise.all(ids.map(async (coachId) => {
          const [profile, v] = await Promise.all([
            getUserProfile(coachId).catch(() => null),
            getLiveCoachView({ coachId }).catch(() => null),
          ]);
          return { coachId, name: profile?.displayName ?? coachId, v, error: !v };
        }));
      })
      .then(setCoaches)
      .catch(() => setCoachError(true));
  }, []);

  const loadMonitoring = useCallback(() => {
    getLiveMonitoring({}).then(setMonitoring).catch(() => setMonitoringError(true));
  }, []);

  // The caller sets feedBusy for Load More; the first page has the skeleton.
  const loadFeed = useCallback((before?: string) => {
    getLiveAlertFeed({ limit: FEED_PAGE, ...(before && { before }) })
      .then(({ events }) => {
        setFeed((f) => (before && f ? [...f, ...events] : events));
        setFeedMore(events.length === FEED_PAGE);
      })
      .catch(() => setFeedError(true))
      .finally(() => setFeedBusy(false));
  }, []);

  const loadMemberships = useCallback(() => {
    getDocs(query(collection(db, 'liveMemberships'),
      where('status', 'in', ['active', 'cancelling', 'paused', 'pending_payment'])))
      .then((snap) => setMemberships(snap.docs.map((d) => {
        const m = d.data();
        return {
          id: d.id, playerName: m.playerName ?? m.playerId, coachName: m.coachName ?? m.coachId, coachId: m.coachId,
          status: m.status, creditsUsed: m.creditsUsed ?? 0, creditsRemaining: m.creditsRemaining ?? 0,
          cycleEndAt: m.cycleEndAt,
        };
      })))
      .catch(() => setMemberships([]));
  }, []);

  const loadView = useCallback((id: string) => {
    getLiveMembershipAdminView({ membershipId: id })
      .then(setView)
      .catch((err) => setViewError(liveLessonsErrorMessage(err, 'Could not load this Monthly Coaching.')));
  }, []);

  useEffect(() => {
    loadCoaches(); loadMonitoring(); loadFeed(); loadMemberships();
  }, [loadCoaches, loadMonitoring, loadFeed, loadMemberships]);
  useEffect(() => { if (selectedMembership) loadView(selectedMembership); }, [selectedMembership, loadView]);

  // Names for the people monitoring and the feed name by id: coaches are
  // already known; players are read once each.
  useEffect(() => {
    const ids = new Set<string>();
    for (const id of Object.keys(monitoring?.byPlayer ?? {})) ids.add(id);
    for (const e of feed ?? []) { ids.add(e.coachId); if (e.playerId) ids.add(e.playerId); }
    const missing = [...ids].filter((id) => id && !(id in names) && !coaches?.some((c) => c.coachId === id));
    if (missing.length === 0) return;
    Promise.all(missing.map(async (id) => [id, (await getUserProfile(id).catch(() => null))?.displayName ?? id] as const))
      .then((pairs) => setNames((n) => ({ ...n, ...Object.fromEntries(pairs) })));
  }, [monitoring, feed, coaches, names]);
  const nameOf = (id: string | null) =>
    !id ? '-' : coaches?.find((c) => c.coachId === id)?.name ?? names[id] ?? id;

  // Coach misses first, then the most members falling behind. An ordering
  // of the server's counts only; nothing follows from it.
  const ranked = useMemo(() => [...(coaches ?? [])].sort((a, b) => {
    const va = a.v; const vb = b.v;
    if (!va || !vb) return va ? -1 : vb ? 1 : 0;
    return vb.counts.coachMisses - va.counts.coachMisses ||
      vb.membersBehind.length - va.membersBehind.length;
  }), [coaches]);
  const coach = ranked.find((c) => c.coachId === selectedCoach) ?? null;
  const membership = memberships?.find((m) => m.id === selectedMembership) ?? null;

  const scopeRows = (): Array<{ key: string; label: string; c: LiveCounts }> => {
    if (!monitoring) return [];
    if (scope === 'month') {
      return Object.entries(monitoring.byMonth).sort(([a], [b]) => b.localeCompare(a))
        .map(([k, c]) => ({ key: k, label: monthLabel(k), c }));
    }
    if (scope === 'playerMonth') {
      return Object.entries(monitoring.byPlayerMonth)
        .flatMap(([pid, months]) => Object.entries(months).map(([k, c]) => ({ key: `${pid}-${k}`, label: `${nameOf(pid)}, ${monthLabel(k)}`, c, sort: `${nameOf(pid)} ${k}` })))
        .sort((a, b) => a.sort.localeCompare(b.sort));
    }
    const by = scope === 'coach' ? monitoring.byCoach : monitoring.byPlayer;
    return Object.entries(by).map(([id, c]) => ({ key: id, label: nameOf(id), c }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const afterAction = () => {
    setAction(null);
    loadCoaches();
    loadMemberships();
    if (selectedMembership) loadView(selectedMembership);
  };

  const skeleton = (n: number) => (
    <div className="space-y-3 px-4 py-4">{Array.from({ length: n }, (_, i) => <Skeleton key={i} width="100%" height={20} radius={4} />)}</div>
  );

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 p-6">
      <div>
        <h1 className="text-[22px] leading-[28px] font-bold text-gray-900">Live Lessons</h1>
        <p className={`mt-0.5 ${FOOTNOTE}`}>
          Monitoring, coaches, Monthly Coaching and the manual actions. Nothing here acts on its own. Every action needs a note and is audited.
        </p>
      </div>

      {/* ── Monitoring ────────────────────────────────────────────────── */}
      <section className={CARD} data-testid="monitoring">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div>
            <h2 className={H2}>Monitoring</h2>
            <p className={FOOTNOTE}>Lessons count in the month they were scheduled, in the player's time zone. Rates are misses over scheduled lessons.</p>
          </div>
          <div className="w-full max-w-[520px]">
            <SegmentedControl<Scope>
              ariaLabel="Group by"
              value={scope}
              onChange={setScope}
              segments={[
                { value: 'month', label: 'By Month' },
                { value: 'coach', label: 'By Coach' },
                { value: 'player', label: 'By Player' },
                { value: 'playerMonth', label: 'Player by Month' },
              ]}
            />
          </div>
        </div>
        {monitoringError ? (
          <p className={`px-4 py-6 ${FOOTNOTE}`}>
            Could not load monitoring.{' '}
            <button type="button" onClick={() => { setMonitoringError(false); loadMonitoring(); }} className="text-biscayne">Try Again</button>
          </p>
        ) : !monitoring ? skeleton(4) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1480px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className={TH}>{scope === 'month' ? 'Month' : scope === 'coach' ? 'Coach' : scope === 'player' ? 'Player' : 'Player, month'}</th>
                  {COUNT_COLUMNS.map((col) => <th key={col.key} className={`${TH} text-right`}>{col.label}</th>)}
                </tr>
              </thead>
              <tbody>
                <CountsRow label="All time" c={monitoring.allTime} strong />
                {scopeRows().map((r) => <CountsRow key={r.key} label={r.label} c={r.c} />)}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Alert feed ────────────────────────────────────────────────── */}
      <section className={CARD} data-testid="alert-feed">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className={H2}>Alert feed</h2>
          <p className={FOOTNOTE}>Each event as it happens, newest first.</p>
        </div>
        {feedError ? (
          <p className={`px-4 py-6 ${FOOTNOTE}`}>
            Could not load the feed.{' '}
            <button type="button" onClick={() => { setFeedError(false); setFeed(null); loadFeed(); }} className="text-biscayne">Try Again</button>
          </p>
        ) : feed === null ? skeleton(4) : feed.length === 0 ? (
          <p className={`px-4 py-6 ${FOOTNOTE}`}>No events yet.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px]">
                <thead className="bg-gray-50">
                  <tr><th className={TH}>When</th><th className={TH}>Event</th><th className={TH}>Coach</th><th className={TH}>Player</th><th className={TH}>Lesson</th></tr>
                </thead>
                <tbody>
                  {feed.map((e) => (
                    <tr key={e.id} className="border-t border-gray-100">
                      <td className={`${TD} whitespace-nowrap`}>{when(e.at)}</td>
                      <td className={TD}>{e.text}</td>
                      <td className={TD}>{nameOf(e.coachId)}</td>
                      <td className={TD}>{nameOf(e.playerId)}</td>
                      <td className={`${TD} whitespace-nowrap text-gray-500`}>{when(e.lessonStartAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {feedMore && (
              <div className="border-t border-gray-100 px-4 py-3">
                <button type="button" onClick={() => { setFeedBusy(true); loadFeed(feed[feed.length - 1].at); }} disabled={feedBusy}
                  aria-busy={feedBusy} style={busyStyle(feedBusy)} className={BTN_SECONDARY_SM}>
                  {feedBusy ? <ButtonSpinner size={16} /> : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Coaches ───────────────────────────────────────────────────── */}
      <section className={CARD} data-testid="coaches">
        <div className="flex items-baseline justify-between gap-4 border-b border-gray-200 px-4 py-3">
          <h2 className={H2}>Coaches</h2>
          <span className={FOOTNOTE}>Coach misses first, then members behind</span>
        </div>
        {coachError ? (
          <p className={`px-4 py-6 ${FOOTNOTE}`}>
            Could not load coaches.{' '}
            <button type="button" onClick={() => { setCoachError(false); setCoaches(null); loadCoaches(); }} className="text-biscayne">Try Again</button>
          </p>
        ) : coaches === null ? skeleton(3) : ranked.length === 0 ? (
          <p className={`px-4 py-6 ${FOOTNOTE}`}>No coach offers live lessons yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className={TH}>Coach</th><th className={TH}>Scheduled</th><th className={TH}>Coach misses</th>
                  <th className={TH}>Coach rate</th><th className={TH}>Returned</th>
                  <th className={TH}>Open slots / unused credits</th><th className={TH}>Members / cap</th><th className={TH}>Members behind</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((c) => (
                  <tr key={c.coachId} onClick={() => setSelectedCoach(c.coachId === selectedCoach ? null : c.coachId)}
                    className={`cursor-pointer border-t border-gray-100 ${c.coachId === selectedCoach ? 'bg-[#EAF4F7]' : 'hover:bg-gray-50'}`}>
                    <td className={`${TD} font-medium`}>{c.name}</td>
                    {c.v ? (
                      <>
                        <td className={TD}>{c.v.counts.scheduled}</td>
                        <td className={TD} style={c.v.counts.coachMisses ? { color: DANGER } : undefined}>{c.v.counts.coachMisses}</td>
                        <td className={TD}>{pct(c.v.counts.coachMissRate)}</td>
                        <td className={TD}>{c.v.counts.returned}</td>
                        <td className={TD} style={c.v.openSlots < c.v.membersUnusedCredits ? { color: DANGER } : undefined}>
                          {c.v.openSlots} / {c.v.membersUnusedCredits}
                        </td>
                        <td className={TD}>{c.v.members} / {c.v.cap ?? 'no cap'}</td>
                        <td className={TD} style={c.v.membersBehind.length ? { color: DANGER } : undefined}>{c.v.membersBehind.length}</td>
                      </>
                    ) : (
                      <td className={`${TD} text-gray-500`} colSpan={7}>Could not load.</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {coach?.v && (
        <section className={CARD} data-testid="coach-view">
          <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-3">
            <h2 className={H2}>{coach.name}</h2>
            <button type="button" className={BTN_SECONDARY_SM} style={{ color: DANGER }}
              onClick={() => setAction({ action: 'deduct_from_next_payout', coachId: coach.coachId })}>
              Deduct From Next Payout
            </button>
          </div>
          <div className="px-4 pt-4">
            <h3 className="text-[15px] leading-[20px] font-semibold text-gray-900">Members behind</h3>
            <p className={FOOTNOTE}>Unused credits above the open slots left before the member's period ends. Offering a switch is manual.</p>
          </div>
          {coach.v.membersBehind.length === 0 ? (
            <p className={`px-4 py-3 ${FOOTNOTE}`}>No member is short of open slots.</p>
          ) : (
            <table className="mt-2 w-full">
              <thead className="bg-gray-50">
                <tr><th className={TH}>Player</th><th className={TH}>Unused credits</th><th className={TH}>Open slots</th><th className={TH}>Period ends</th></tr>
              </thead>
              <tbody>
                {coach.v.membersBehind.map((m) => (
                  <tr key={m.membershipId} className="border-t border-gray-100">
                    <td className={`${TD} font-medium`}>{m.playerName ?? nameOf(m.playerId)}</td>
                    <td className={TD}>{m.unusedCredits}</td>
                    <td className={TD} style={{ color: DANGER }}>{m.openSlots}</td>
                    <td className={TD}>{when(m.periodEnd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="border-t border-gray-200 px-4 pt-4">
            <h3 className="text-[15px] leading-[20px] font-semibold text-gray-900">Events</h3>
          </div>
          {coach.v.events.length === 0 ? (
            <p className={`px-4 py-3 ${FOOTNOTE}`}>No events yet.</p>
          ) : (
            <table className="mt-2 w-full">
              <thead className="bg-gray-50"><tr><th className={TH}>When</th><th className={TH}>Event</th><th className={TH}>Player</th></tr></thead>
              <tbody>
                {coach.v.events.map((e) => (
                  <tr key={e.id} className="border-t border-gray-100">
                    <td className={`${TD} whitespace-nowrap`}>{when(e.at)}</td>
                    <td className={TD}>{eventText(e)}</td>
                    <td className={TD}>{nameOf(e.playerId ?? null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* ── Memberships ───────────────────────────────────────────────── */}
      <section className={CARD} data-testid="memberships">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className={H2}>Monthly Coaching</h2>
        </div>
        {memberships === null ? (
          <div className="space-y-3 px-4 py-4">{[0, 1, 2].map((i) => <Skeleton key={i} width="100%" height={20} radius={4} />)}</div>
        ) : memberships.length === 0 ? (
          <p className={`px-4 py-6 ${FOOTNOTE}`}>No Monthly Coaching.</p>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr><th className={TH}>Player</th><th className={TH}>Coach</th><th className={TH}>Status</th><th className={TH}>Credits used / left</th><th className={TH}>Period ends</th></tr>
            </thead>
            <tbody>
              {memberships.map((m) => (
                <tr key={m.id} onClick={() => {
                  setView(null);
                  setViewError(null);
                  setSelectedMembership(m.id === selectedMembership ? null : m.id);
                }}
                  className={`cursor-pointer border-t border-gray-100 ${m.id === selectedMembership ? 'bg-[#EAF4F7]' : 'hover:bg-gray-50'}`}>
                  <td className={`${TD} font-medium`}>{m.playerName}</td>
                  <td className={TD}>{m.coachName}</td>
                  <td className={TD}>{m.status}</td>
                  <td className={TD}>{m.creditsUsed} / {m.creditsRemaining}</td>
                  <td className={TD}>{when(m.cycleEndAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {membership && (
        <section className={CARD} data-testid="membership-view">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
            <h2 className={H2}>{membership.playerName} with {membership.coachName}</h2>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={BTN_SECONDARY_SM} onClick={() => setAction({ action: 'add_credits', membershipId: membership.id })}>Add Credits</button>
              <button type="button" className={BTN_SECONDARY_SM} onClick={() => setAction({ action: 'restore_credit', membershipId: membership.id })}>Restore Credit</button>
              {view?.billing.billingPause ? (
                <button type="button" className={BTN_SECONDARY_SM} onClick={() => setAction({ action: 'resume_billing', membershipId: membership.id })}>Resume Billing</button>
              ) : (
                <button type="button" className={BTN_SECONDARY_SM} onClick={() => setAction({ action: 'pause_billing', membershipId: membership.id })}>Pause Billing</button>
              )}
              <button type="button" className={BTN_SECONDARY_SM} onClick={() => setAction({ action: 'offer_transfer', membershipId: membership.id, coachId: membership.coachId })}>Offer Transfer</button>
              <button type="button" className={BTN_SECONDARY_SM} onClick={() => setAction({ action: 'move_membership', membershipId: membership.id, coachId: membership.coachId })}>Move Monthly Coaching</button>
              <button type="button" className={BTN_SECONDARY_SM} style={{ color: DANGER }} onClick={() => setAction({ action: 'refund_remaining_credits', membershipId: membership.id })}>Refund Remaining Credits</button>
            </div>
          </div>

          {viewError ? (
            <p className={`px-4 py-6 ${FOOTNOTE}`}>{viewError}</p>
          ) : !view ? (
            <div className="space-y-3 px-4 py-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} width="60%" height={18} radius={4} />)}</div>
          ) : (
            <div className="grid gap-6 p-4 md:grid-cols-2">
              <div>
                <h3 className="mb-2 text-[15px] leading-[20px] font-semibold text-gray-900">Credits</h3>
                <dl className="space-y-1 text-[15px] leading-[20px]">
                  <div className="flex justify-between"><dt className="text-gray-500">This period</dt><dd>{view.credits.label}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Used / left</dt><dd className="tabular-nums">{view.credits.used} / {view.credits.remaining}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Rolled</dt><dd>{view.credits.rolled ? `${view.credits.rolled.count}, expires ${when(view.credits.rolled.expiresAt)}` : 'None'}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Flagged</dt><dd>{view.credits.flagged ? `${view.credits.flagged.count}, expire ${when(view.credits.flagged.expiresAt)}` : 'None'}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Expired</dt><dd className="tabular-nums">{view.credits.expired}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-500">From Double Bagel</dt><dd className="text-right">{view.credits.fromDoubleBagel.thisPeriod} this period, {view.credits.fromDoubleBagel.unused} unused{view.credits.fromDoubleBagel.nextPeriod ? `, ${view.credits.fromDoubleBagel.nextPeriod} next period` : ''}</dd></div>
                </dl>
              </div>
              <div>
                <h3 className="mb-2 text-[15px] leading-[20px] font-semibold text-gray-900">Billing</h3>
                <dl className="space-y-1 text-[15px] leading-[20px]">
                  <div className="flex justify-between"><dt className="text-gray-500">Status</dt><dd>{view.billing.status}{view.billing.billingPause ? `, paused (${view.billing.billingPause.reason})` : ''}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Locked price</dt><dd className="tabular-nums">{formatPriceExact(view.billing.lockedPriceCents)}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Period 1 started</dt><dd>{view.billing.periodAnchoredAt ? `${when(view.billing.periodAnchoredAt)} (${view.billing.anchorReason})` : 'Not yet'}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">This period</dt><dd>{when(view.billing.cycleStartAt)} to {when(view.billing.cycleEndAt)}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Subscription</dt><dd className="font-mono text-[13px]">{view.billing.stripeSubscriptionId ?? 'None yet'}</dd></div>
                  {view.billing.paymentFailedAt && <div className="flex justify-between"><dt className="text-gray-500">Payment failed</dt><dd style={{ color: DANGER }}>{when(view.billing.paymentFailedAt)}, retrying</dd></div>}
                  {view.billing.lapsedAt && <div className="flex justify-between"><dt className="text-gray-500">Lapsed</dt><dd>{when(view.billing.lapsedAt)}</dd></div>}
                  {view.billing.refundedAt && <div className="flex justify-between"><dt className="text-gray-500">Refunded</dt><dd>{when(view.billing.refundedAt)}</dd></div>}
                </dl>
              </div>
              <div>
                <h3 className="mb-2 text-[15px] leading-[20px] font-semibold text-gray-900">Payouts</h3>
                {view.periodPayouts.length === 0 ? <p className={FOOTNOTE}>No period payouts.</p> : (
                  <ul className="space-y-1 text-[15px] leading-[20px]">
                    {view.periodPayouts.map((p) => (
                      <li key={p.id} className="flex justify-between gap-3">
                        <span className="text-gray-500">
                          Period {p.periodNumber ?? '-'}{p.part ? `, ${p.part === 'first' ? 'first lesson' : 'rest of period'}` : ''}, {p.status.replace(/_/g, ' ')}
                        </span>
                        <span className="tabular-nums">{formatPriceExact(p.coachCents)}{p.releaseAt ? `, ${when(p.releaseAt)}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <h3 className="mb-2 mt-4 text-[15px] leading-[20px] font-semibold text-gray-900">Deductions</h3>
                {view.payouts.adjustments.length === 0 ? <p className={FOOTNOTE}>None.</p> : (
                  <ul className="space-y-2 text-[15px] leading-[20px]">
                    {view.payouts.adjustments.map((a) => (
                      <li key={a.id} className="flex items-center justify-between gap-3">
                        <span className="text-gray-500">{a.reason.replace(/_/g, ' ')}, {a.status}</span>
                        <span className="flex items-center gap-3 tabular-nums">
                          {a.amountCents < 0 ? `-${formatPriceExact(-a.amountCents)}` : formatPriceExact(a.amountCents)}
                          {a.status === 'pending' && (
                            <button type="button" className={BTN_SECONDARY_SM}
                              onClick={() => setAction({ action: 'waive_deduction', adjustmentId: a.id, membershipId: membership.id })}>
                              Waive
                            </button>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 className="mb-2 text-[15px] leading-[20px] font-semibold text-gray-900">Lessons</h3>
                {view.payouts.bookings.length === 0 ? <p className={FOOTNOTE}>No lessons booked.</p> : (
                  <ul className="space-y-2 text-[15px] leading-[20px]">
                    {[...view.payouts.bookings].sort((a, b) => b.scheduledStartAt.localeCompare(a.scheduledStartAt)).map((b) => (
                      <li key={b.id} className="flex items-center justify-between gap-3">
                        <span className="tabular-nums">{when(b.scheduledStartAt)}</span>
                        <span className="text-gray-500">{b.status.replace(/_/g, ' ')}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {action && <ActionSheet params={action} coaches={ranked} onClose={() => setAction(null)} onDone={afterAction} />}
    </div>
  );
}
